import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { EditorialNav } from '../components/editorial/EditorialNav';
import { EditorialFooter } from '../components/editorial/EditorialFooter';
import { APPLY_URL } from '../mock';

// Restrained, slow fade-up on scroll
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1] } }
};

// SIGNATURE INTERACTION — slow clip-path reveal of the hero image
const heroImageReveal = {
  hidden: { clipPath: 'inset(0% 50% 0% 50%)', scale: 1.08 },
  show: {
    clipPath: 'inset(0% 0% 0% 0%)',
    scale: 1,
    transition: { duration: 1.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const HomeEditorial = () => {
  return (
    <div className="bg-[#141416] text-[#f4efe6] min-h-screen font-sans-clean overflow-x-hidden">
      <EditorialNav />

      {/* HERO — asymmetric editorial; signature slow image reveal */}
      <section className="relative h-[100svh] min-h-[640px] overflow-hidden">
        {/* Photography — full bleed */}
        <motion.div
          variants={heroImageReveal}
          initial="hidden"
          animate="show"
          className="absolute inset-0 overflow-hidden"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_driven-12week/artifacts/fiypz4d4_IMG_0505.jpeg"
            alt="Clifton Tant — CT3 Performance"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover editorial-photo"
            style={{ objectPosition: 'center top' }}
          />
          {/* Cinematic gradient — heavier at bottom-left where copy lives */}
          <div className="absolute inset-0 bg-gradient-to-tr from-[#141416] via-[#141416]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#141416]/90 via-transparent to-[#141416]/30" />
        </motion.div>

        {/* Editorial corner marks */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="absolute top-28 md:top-36 right-6 md:right-10 z-10 flex items-center gap-3"
        >
          <div className="h-px w-10 md:w-16 bg-[#c6a04a]" />
          <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#c6a04a]">
            Est. Nashville
          </p>
        </motion.div>

        {/* Hero copy — bottom-left, asymmetric (not centered) */}
        <div className="absolute inset-0 z-10 grid grid-cols-12 items-end pb-16 md:pb-24">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { delayChildren: 0.6, staggerChildren: 0.12 } } }}
            className="col-span-12 md:col-span-8 lg:col-span-7 px-6 md:px-10 lg:pl-16"
          >
            <motion.p
              variants={fadeUp}
              className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-8 md:mb-10"
            >
              CT3 Performance
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="font-editorial text-[2.6rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] xl:text-[7.5rem] leading-[0.92] tracking-[-0.035em] text-[#f4efe6] mb-8 md:mb-10"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
            >
              Health that keeps pace<br />
              with your <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70, "wght" 360' }}>ambition.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="font-sans-clean text-[15px] md:text-[17px] text-[#f4efe6]/70 max-w-md md:max-w-lg leading-[1.65] mb-10 md:mb-12"
            >
              Systems that keep high-output people — and the teams they lead — lean, strong, and clear.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 bg-[#c6a04a] hover:bg-[#d9be7e] text-[#141416] font-sans-clean text-[12px] md:text-[13px] font-medium tracking-[0.18em] uppercase px-7 py-4 transition-all"
              >
                Apply — 1:1 Coaching
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </a>
              <Link
                to="/organizations"
                className="group inline-flex items-center justify-center gap-2.5 border border-[#f4efe6]/30 hover:border-[#c6a04a] text-[#f4efe6] hover:text-[#c6a04a] font-sans-clean text-[12px] md:text-[13px] font-medium tracking-[0.18em] uppercase px-7 py-4 transition-all"
              >
                For Organizations
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM — single editorial line, hand-tuned hanging margin */}
      <section className="bg-[#141416] py-28 md:py-44 px-6 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 md:col-span-10 md:col-start-2"
          >
            <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-8 md:mb-10">
              The Premise
            </p>
            <p
              className="font-editorial text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl leading-[1.15] tracking-[-0.02em] text-[#f4efe6]/95"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 40, "wght" 380' }}
            >
              High performers optimize <span className="text-[#f4efe6]/55">everything</span> — except the body that carries it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* METHOD — asymmetric 3-pillar (broken grid) */}
      <section className="bg-[#0e0e10] py-28 md:py-40 px-6 md:px-10 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 mb-20 md:mb-28"
          >
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-6">
                Method
              </p>
              <h2
                className="font-editorial text-[2rem] sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.03em] text-[#f4efe6]"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
              >
                Three principles. <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>Zero guesswork.</span>
              </h2>
            </div>
          </motion.div>

          {/* Asymmetric staggered grid */}
          <div className="grid grid-cols-12 gap-y-16 md:gap-y-24">
            {[
              {
                no: '01',
                title: 'Structure over motivation',
                body: 'Motivation runs out. Systems compound. CT3 installs the structure that holds when the calendar doesn\'t.',
                cols: 'col-span-12 md:col-span-5 md:col-start-2'
              },
              {
                no: '02',
                title: 'Built around your schedule',
                body: 'Training, nutrition, and recovery designed for travel, deadlines, and fifty-hour weeks.',
                cols: 'col-span-12 md:col-span-4 md:col-start-8'
              },
              {
                no: '03',
                title: 'Measured by health markers',
                body: 'Bloodwork, body composition, recovery, performance. Real signals — not the scale.',
                cols: 'col-span-12 md:col-span-5 md:col-start-4'
              }
            ].map((p, i) => (
              <motion.div
                key={p.no}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: i * 0.08, duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
                className={`${p.cols} space-y-5`}
              >
                <p
                  className="font-editorial-italic text-[#c6a04a] text-2xl md:text-3xl"
                  style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}
                >
                  {p.no}
                </p>
                <div className="h-px w-10 bg-[#c6a04a]/50" />
                <h3
                  className="font-editorial text-[1.4rem] md:text-3xl leading-[1.1] tracking-[-0.02em] text-[#f4efe6]"
                  style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
                >
                  {p.title}
                </h3>
                <p className="font-sans-clean text-[15px] text-[#f4efe6]/60 leading-[1.7] max-w-sm">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TWO PATHS — asymmetric vertical stagger */}
      <section className="bg-[#141416] py-28 md:py-40 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 mb-16 md:mb-24"
          >
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-6">
                Two Paths
              </p>
              <h2
                className="font-editorial text-[2rem] sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.03em] text-[#f4efe6]"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
              >
                One <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>standard.</span>
              </h2>
            </div>
          </motion.div>

          <div className="grid grid-cols-12 gap-y-6">
            {/* Individuals — offset top */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="col-span-12 md:col-span-6 md:col-start-1 group border-t border-[#c6a04a]/30 pt-10 md:pt-12 pr-0 md:pr-12"
            >
              <p className="font-sans-clean text-[10px] uppercase tracking-[0.35em] text-[#c6a04a]/80 mb-8">
                For Individuals
              </p>
              <h3
                className="font-editorial text-[1.75rem] md:text-4xl leading-[1.1] tracking-[-0.02em] text-[#f4efe6] mb-6"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 35, "wght" 400' }}
              >
                Premium 1:1 coaching for high-output professionals.
              </h3>
              <p className="font-sans-clean text-[15px] text-[#f4efe6]/60 leading-[1.7] mb-10 max-w-md">
                Personalized programming, weekly accountability, a system built around your life.
              </p>
              <a
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group/cta inline-flex items-center gap-2 text-[#c6a04a] font-sans-clean text-[12px] uppercase tracking-[0.2em] hover:gap-3 transition-all"
              >
                Apply
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </a>
            </motion.div>

            {/* Organizations — vertically offset down for asymmetry */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="col-span-12 md:col-span-5 md:col-start-8 md:mt-32 group border-t border-[#c6a04a]/30 pt-10 md:pt-12"
            >
              <p className="font-sans-clean text-[10px] uppercase tracking-[0.35em] text-[#c6a04a]/80 mb-8">
                For Organizations
              </p>
              <h3
                className="font-editorial text-[1.75rem] md:text-4xl leading-[1.1] tracking-[-0.02em] text-[#f4efe6] mb-6"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 35, "wght" 400' }}
              >
                Performance health for teams and institutions.
              </h3>
              <p className="font-sans-clean text-[15px] text-[#f4efe6]/60 leading-[1.7] mb-10 max-w-md">
                Structured programming designed for faculty, staff, and high-performing teams. Built for rollout.
              </p>
              <Link
                to="/organizations"
                className="inline-flex items-center gap-2 text-[#c6a04a] font-sans-clean text-[12px] uppercase tracking-[0.2em] hover:gap-3 transition-all"
              >
                Partner with CT3
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FOUNDER — full signage photo (left), pull quote (right) */}
      <section className="bg-[#f4efe6] text-[#141416] grid grid-cols-12 min-h-[80vh]">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="col-span-12 md:col-span-6 lg:col-span-5 relative min-h-[60vh] md:min-h-[80vh] overflow-hidden bg-[#141416] flex items-center justify-center p-6 md:p-10"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_driven-12week/artifacts/6e48hpw4_0762C952-E703-45A1-BED6-946238B6C2DC.png"
            alt="CT3 Performance — Studio Office"
            loading="lazy"
            decoding="async"
            className="block max-w-full max-h-full w-auto h-auto object-contain editorial-photo"
          />
        </motion.div>
        <div className="col-span-12 md:col-span-6 lg:col-span-7 flex items-center px-6 md:px-12 lg:px-20 py-20 md:py-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.85, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl"
          >
            <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-8 md:mb-10">
              The Founder
            </p>
            <p
              className="font-editorial text-[1.65rem] sm:text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.2] tracking-[-0.02em] text-[#141416] mb-10"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 40, "wght" 380' }}
            >
              "Generic programs are built for people with unlimited time. The people I work with don't have that — and they don't need it. <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>They need a system.</span>"
            </p>
            <div className="space-y-0.5">
              <p className="font-sans-clean text-sm font-medium text-[#141416] tracking-wide">
                Clifton Tant
              </p>
              <p className="font-sans-clean text-[12px] text-[#141416]/55 uppercase tracking-[0.18em]">
                Founder · CT3 Performance
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-[#141416] py-28 md:py-40 px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-5xl mx-auto"
        >
          <h2
            className="font-editorial text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em] text-[#f4efe6] mb-12 md:mb-16 max-w-3xl"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
          >
            Build the system that <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>outlasts</span> the goal.
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2.5 bg-[#c6a04a] hover:bg-[#d9be7e] text-[#141416] font-sans-clean text-[12px] md:text-[13px] font-medium tracking-[0.18em] uppercase px-7 py-4 transition-all"
            >
              Apply — 1:1 Coaching
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
            </a>
            <Link
              to="/organizations"
              className="group inline-flex items-center justify-center gap-2.5 border border-[#f4efe6]/30 hover:border-[#c6a04a] text-[#f4efe6] hover:text-[#c6a04a] font-sans-clean text-[12px] md:text-[13px] font-medium tracking-[0.18em] uppercase px-7 py-4 transition-all"
            >
              For Organizations
              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2} />
            </Link>
          </div>
        </motion.div>
      </section>

      <EditorialFooter />
    </div>
  );
};

export default HomeEditorial;
